import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import dayjs from 'dayjs';

import { fetchClientOffers, createClientOffer, updateClientOffer, deleteClientOffer, fetchClientOfferOptions, createClientOfferOption } from '@src/api';
import { fetchVerticals } from '@src/api/actions/verticals';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';

const OFFER_TYPES = [
  { value: 'offer', label: 'Ad Offer' },
  { value: 'lp_offer', label: 'LP Offer' },
  { value: 'financing', label: 'Financing Offer' },
];

const EMPTY_OFFER = {
  client: '',
  offer_type: 'offer',
  vertical: '',
  ad_offer_option: '',
  lp_offer_option: '',
  financing_offer_option: '',
  custom_label: '',
  description: '',
  financing_details: '',
  expires_at: '',
};

export default function ClientOffersPage() {
  const OPTION_FIELD_MAP = {
    offer: 'ad_offer_option',
    lp_offer: 'lp_offer_option',
    financing: 'financing_offer_option',
  };

  const [offers, setOffers] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [filterClient, setFilterClient] = useState('');
  const [filterVertical, setFilterVertical] = useState('');
  const [filterType, setFilterType] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [current, setCurrent] = useState(EMPTY_OFFER);
  const [options, setOptions] = useState([]);
  const [optionDialogOpen, setOptionDialogOpen] = useState(false);
  const [optionSaving, setOptionSaving] = useState(false);
  const [newOption, setNewOption] = useState({ offer_type: 'offer', label: '', sort_order: 0, active: true });

  const {
    options: clientOptions,
    loading: clientOptionsLoading,
    handleInputChange: handleClientSearch,
    findOptionByValue: findClientOptionByValue,
    addOption: addClientOption,
  } = useClientAutocomplete();

  const loadVerticals = async () => {
    try {
      const res = await fetchVerticals();
      setVerticals(res.data || []);
    } catch (_e) {
      // ignore
    }
  };

  useEffect(() => {
    loadVerticals();
  }, []);

  const loadOffers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchClientOffers({
        client_id: filterClient || undefined,
        vertical_id: filterVertical || undefined,
        offer_type: filterType || undefined,
        search: search || undefined,
      });
      const data = res.data || [];
      data.forEach((o) => {
        if (o.client && o.client_name) {
          addClientOption({ value: o.client, label: o.client_name });
        }
      });
      setOffers(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOffers();
  }, []);

  const loadOptionsForType = async (offerType) => {
    try {
      const res = await fetchClientOfferOptions({ offer_type: offerType });
      setOptions(res.data || []);
    } catch (_e) {
      setOptions([]);
    }
  };

  useEffect(() => {
    loadOptionsForType(current.offer_type);
  }, [current.offer_type]);

  const handleOpen = (offer = EMPTY_OFFER) => {
    setCurrent({
      ...EMPTY_OFFER,
      ...offer,
      client: offer.client || offer.client_id || '',
      vertical: offer.vertical || offer.vertical_id || '',
      ad_offer_option: offer.ad_offer_option || '',
      lp_offer_option: offer.lp_offer_option || '',
      financing_offer_option: offer.financing_offer_option || '',
    });
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setCurrent(EMPTY_OFFER);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    const payload = { ...current };
    const requiredOptionField = OPTION_FIELD_MAP[current.offer_type];
    if (!payload[requiredOptionField]) {
      setError('Select an offer option.');
      setSaving(false);
      return;
    }
    payload.custom_label = '';
    if (!payload.expires_at) {
      payload.expires_at = null;
    }
    try {
      if (current.id) {
        await updateClientOffer(current.id, payload);
      } else {
        await createClientOffer(payload);
      }
      handleClose();
      await loadOffers();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to save offer');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this offer?')) return;
    setError('');
    try {
      await deleteClientOffer(id);
      await loadOffers();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to delete offer');
    }
  };

  const visibleOffers = useMemo(() => offers, [offers]);

  const optionField = {
    offer: 'ad_offer_option',
    lp_offer: 'lp_offer_option',
    financing: 'financing_offer_option',
  }[current.offer_type];

  const optionValue = current[optionField];

  const clientLabelForRow = (offer) => {
    if (offer.client_name) return offer.client_name;
    const match = findClientOptionByValue(offer.client);
    return match?.label || 'Unknown client';
  };

  const getVerticalName = (offer) => offer.vertical_name || verticals.find((v) => String(v.id) === String(offer.vertical))?.vertical || '';

  const handleSaveOption = async () => {
    setOptionSaving(true);
    setError('');
    try {
      const res = await createClientOfferOption(newOption);
      const created = res.data;
      setOptionDialogOpen(false);
      setNewOption({ offer_type: 'offer', label: '', sort_order: 0, active: true });
      if (newOption.offer_type === current.offer_type) {
        loadOptionsForType(current.offer_type);
        if (created?.id) {
          const field = OPTION_FIELD_MAP[newOption.offer_type];
          setCurrent((c) => ({ ...c, [field]: created.id }));
        }
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to create option');
    } finally {
      setOptionSaving(false);
    }
  };

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={700}>
          Client Offers
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadOffers} disabled={loading}>
            Refresh
          </Button>
          <Button variant="outlined" onClick={() => setOptionDialogOpen(true)}>
            Add Offer Option
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
            Add Offer
          </Button>
        </Stack>
      </Stack>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Offer label"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Offer type"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {OFFER_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Vertical"
                value={filterVertical}
                onChange={(e) => setFilterVertical(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {verticals.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.vertical}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                fullWidth
                options={clientOptions}
                loading={clientOptionsLoading}
                value={findClientOptionByValue(filterClient) || null}
                onChange={(_, option) => setFilterClient(option?.value || '')}
                onInputChange={handleClientSearch}
                isOptionEqualToValue={(option, value) => option.value === value?.value}
                getOptionLabel={(option) => option?.label || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    placeholder="All clients"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {clientOptionsLoading ? (
                            <CircularProgress color="inherit" size={16} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1} mt={2}>
            <Button variant="outlined" onClick={loadOffers} disabled={loading}>
              Apply filters
            </Button>
            <Button
              variant="text"
              startIcon={<ClearIcon />}
              onClick={() => {
                setFilterClient('');
                setFilterType('');
                setFilterVertical('');
                setSearch('');
                loadOffers();
              }}
            >
              Clear
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <TableContainer component={Card} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Parent</TableCell>
              <TableCell>Offer</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Vertical</TableCell>
              <TableCell>Expires</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleOffers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary">
                    {loading ? 'Loading…' : 'No offers found.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibleOffers.map((offer) => (
                <TableRow key={offer.id}>
                  <TableCell>{clientLabelForRow(offer)}</TableCell>
                  <TableCell>{offer.parent_company_name || '—'}</TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography fontWeight={600}>{offer.display_label || offer.custom_label || '—'}</Typography>
                      {offer.description ? (
                        <Typography variant="caption" color="text.secondary">
                          {offer.description}
                        </Typography>
                      ) : null}
                      {offer.financing_details ? (
                        <Typography variant="caption" color="text.secondary">
                          {offer.financing_details}
                        </Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {OFFER_TYPES.find((t) => t.value === offer.offer_type)?.label || offer.offer_type}
                  </TableCell>
                  <TableCell>{offer.vertical_name || getVerticalName(offer) || '—'}</TableCell>
                  <TableCell>{offer.expires_at || '—'}</TableCell>
                  <TableCell>{offer.updated_at ? dayjs(offer.updated_at).format('YYYY-MM-DD') : '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpen(offer)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(offer.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{current.id ? 'Edit Offer' : 'Add Offer'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Autocomplete
              options={clientOptions}
              loading={clientOptionsLoading}
              value={findClientOptionByValue(current.client) || null}
              onChange={(_, option) =>
                setCurrent((c) => ({ ...c, client: option?.value || '' }))
              }
              onInputChange={handleClientSearch}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              getOptionLabel={(option) => option?.label || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {clientOptionsLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              select
              label="Offer type"
              value={current.offer_type}
              onChange={(e) => setCurrent((c) => ({ ...c, offer_type: e.target.value }))}
            >
              {OFFER_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Vertical"
              value={current.vertical}
              onChange={(e) => setCurrent((c) => ({ ...c, vertical: e.target.value }))}
            >
              <MenuItem value="">(None)</MenuItem>
              {verticals.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.vertical}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Offer option"
              value={optionValue}
              onChange={(e) => setCurrent((c) => ({ ...c, [optionField]: e.target.value }))}
              helperText={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box component="span">Choose a preset option.</Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setNewOption((o) => ({ ...o, offer_type: current.offer_type }));
                      setOptionDialogOpen(true);
                    }}
                    aria-label="Add offer option"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Stack>
              }
            >
              <MenuItem value="">(None)</MenuItem>
              {options.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              value={current.description}
              onChange={(e) => setCurrent((c) => ({ ...c, description: e.target.value }))}
              multiline
              minRows={2}
            />
            {current.offer_type === 'financing' ? (
              <TextField
                label="Financing details"
                value={current.financing_details}
                onChange={(e) => setCurrent((c) => ({ ...c, financing_details: e.target.value }))}
              />
            ) : null}
            <TextField
              label="Expires at"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={current.expires_at || ''}
              onChange={(e) => setCurrent((c) => ({ ...c, expires_at: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={optionDialogOpen} onClose={() => setOptionDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Offer Option</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <TextField
              select
              label="Offer type"
              value={newOption.offer_type}
              onChange={(e) => setNewOption((o) => ({ ...o, offer_type: e.target.value }))}
            >
              {OFFER_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Label"
              value={newOption.label}
              onChange={(e) => setNewOption((o) => ({ ...o, label: e.target.value }))}
              required
            />
            <TextField
              label="Sort order"
              type="number"
              value={newOption.sort_order}
              onChange={(e) => setNewOption((o) => ({ ...o, sort_order: Number(e.target.value || 0) }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOptionDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveOption} disabled={optionSaving || !newOption.label.trim()}>
            {optionSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
